// src/state/useProductDataStore.js
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  getIngredients,
  getProductFormulationTypes,
  getProductAccreditationTypes,
  getBrands,
  getRoutineFrequency,
  getRoutineApplicationPeriod,
  getRoutineAmounts,
  getRoutineApplicationAreas,
  getProductCategories
} from '../api/apiService';

/** Default state for reference data and loading/errors (not persisted) */
const initialData = {
  ingredients: [],
  formulationTypes: [],
  accreditationTypes: [],
  brands: [],
  productCategories: [],
  prescriptionFrequencies: [],
  prescriptionApplicationPeriods: [],
  routineAmounts: [],
  routineApplicationAreas: [],
};

const initialLoading = {
  ingredients: false,
  formulationTypes: false,
  accreditationTypes: false,
  brands: false,
  productCategories: false,
  prescriptionFrequencies: false,
  prescriptionApplicationPeriods: false,
  routineAmounts: false,
  routineApplicationAreas: false,
};

const initialErrors = {
  ingredients: null,
  formulationTypes: null,
  accreditationTypes: null,
  brands: null,
  productCategories: null,
  prescriptionFrequencies: null,
  prescriptionApplicationPeriods: null,
  routineAmounts: null,
  routineApplicationAreas: null,
};

export const useProductDataStore = create(
  persist(
    (set, get) => ({
      ...initialData,
      isLoading: initialLoading,
      errors: initialErrors,

      fetchIngredients: async () => {
        const state = get();
        if (state.ingredients.length > 0) return state.ingredients;

        set({ isLoading: { ...state.isLoading, ingredients: true } });
        try {
          const res = await getIngredients();
          const data = Array.isArray(res) ? res : res?.data?.results ?? [];
          set({
            ingredients: data,
            isLoading: { ...state.isLoading, ingredients: false },
            errors: { ...state.errors, ingredients: null },
          });
          return data;
        } catch (error) {
          set({
            isLoading: { ...state.isLoading, ingredients: false },
            errors: { ...state.errors, ingredients: error?.message ?? 'Failed' },
          });
          return [];
        }
      },

      fetchFormulationTypes: async () => {
        const state = get();
        if (state.formulationTypes.length > 0) return state.formulationTypes;

        set({ isLoading: { ...state.isLoading, formulationTypes: true } });
        try {
          const res = await getProductFormulationTypes();
          const data = Array.isArray(res) ? res : res?.data?.results ?? [];
          set({
            formulationTypes: data,
            isLoading: { ...state.isLoading, formulationTypes: false },
            errors: { ...state.errors, formulationTypes: null },
          });
          return data;
        } catch (error) {
          set({
            isLoading: { ...state.isLoading, formulationTypes: false },
            errors: { ...state.errors, formulationTypes: error?.message ?? 'Failed' },
          });
          return [];
        }
      },

      fetchAccreditationTypes: async () => {
        const state = get();
        if (state.accreditationTypes.length > 0) return state.accreditationTypes;

        set({ isLoading: { ...state.isLoading, accreditationTypes: true } });
        try {
          const res = await getProductAccreditationTypes();
          const data = Array.isArray(res) ? res : res?.data?.results ?? [];
          set({
            accreditationTypes: data,
            isLoading: { ...state.isLoading, accreditationTypes: false },
            errors: { ...state.errors, accreditationTypes: null },
          });
          return data;
        } catch (error) {
          set({
            isLoading: { ...state.isLoading, accreditationTypes: false },
            errors: { ...state.errors, accreditationTypes: error?.message ?? 'Failed' },
          });
          return [];
        }
      },

      fetchBrands: async () => {
        const state = get();
        if (state.brands.length > 0) return state.brands;

        set({ isLoading: { ...state.isLoading, brands: true } });
        try {
          const res = await getBrands({ page: 1, page_size: 100 });
          const data = Array.isArray(res) ? res : (res?.results ?? []);
          set({
            brands: data,
            isLoading: { ...state.isLoading, brands: false },
            errors: { ...state.errors, brands: null },
          });
          return data;
        } catch (error) {
          set({
            isLoading: { ...state.isLoading, brands: false },
            errors: { ...state.errors, brands: error?.message ?? 'Failed' },
          });
          return [];
        }
      },

      fetchProductCategories: async () => {
        const state = get();
        if (state.productCategories.length > 0) return state.productCategories;

        set({ isLoading: { ...state.isLoading, productCategories: true } });
        try {
          const res = await getProductCategories({ page: 1, page_size: 50 });
          const data = Array.isArray(res) ? res : (res?.results ?? []);
          set({
            productCategories: data,
            isLoading: { ...state.isLoading, productCategories: false },
            errors: { ...state.errors, productCategories: null },
          });
          return data;
        } catch (error) {
          set({
            isLoading: { ...state.isLoading, productCategories: false },
            errors: { ...state.errors, productCategories: error?.message ?? 'Failed' },
          });
          return [];
        }
      },

      fetchPrescriptionFrequencies: async () => {
        const state = get();
        if (state.prescriptionFrequencies.length > 0) return state.prescriptionFrequencies;

        set({ isLoading: { ...state.isLoading, prescriptionFrequencies: true } });
        try {
          const res = await getRoutineFrequency();
          const data = Array.isArray(res) ? res : res?.data?.results ?? [];
          set({
            prescriptionFrequencies: data,
            isLoading: { ...state.isLoading, prescriptionFrequencies: false },
            errors: { ...state.errors, prescriptionFrequencies: null },
          });
          return data;
        } catch (error) {
          set({
            isLoading: { ...state.isLoading, prescriptionFrequencies: false },
            errors: { ...state.errors, prescriptionFrequencies: error?.message ?? 'Failed' },
          });
          return [];
        }
      },

      fetchPrescriptionApplicationPeriods: async () => {
        const state = get();
        if (state.prescriptionApplicationPeriods.length > 0) return state.prescriptionApplicationPeriods;

        set({ isLoading: { ...state.isLoading, prescriptionApplicationPeriods: true } });
        try {
          const res = await getRoutineApplicationPeriod();
          const data = Array.isArray(res) ? res : res?.data?.results ?? [];
          set({
            prescriptionApplicationPeriods: data,
            isLoading: { ...state.isLoading, prescriptionApplicationPeriods: false },
            errors: { ...state.errors, prescriptionApplicationPeriods: null },
          });
          return data;
        } catch (error) {
          set({
            isLoading: { ...state.isLoading, prescriptionApplicationPeriods: false },
            errors: { ...state.errors, prescriptionApplicationPeriods: error?.message ?? 'Failed' },
          });
          return [];
        }
      },

      fetchRoutineAmounts: async () => {
        const state = get();
        if (state.routineAmounts.length > 0) return state.routineAmounts;

        set({ isLoading: { ...state.isLoading, routineAmounts: true } });
        try {
          const res = await getRoutineAmounts({ page: 1, page_size: 100 });
          const data = Array.isArray(res) ? res : (res?.results ?? res?.data?.results ?? []);
          set({
            routineAmounts: data,
            isLoading: { ...state.isLoading, routineAmounts: false },
            errors: { ...state.errors, routineAmounts: null },
          });
          return data;
        } catch (error) {
          set({
            isLoading: { ...state.isLoading, routineAmounts: false },
            errors: { ...state.errors, routineAmounts: error?.message ?? 'Failed' },
          });
          return [];
        }
      },

      fetchRoutineApplicationAreas: async () => {
        const state = get();
        if (state.routineApplicationAreas.length > 0) return state.routineApplicationAreas;

        set({ isLoading: { ...state.isLoading, routineApplicationAreas: true } });
        try {
          const res = await getRoutineApplicationAreas();
          const data = Array.isArray(res) ? res : res?.data?.results ?? [];
          set({
            routineApplicationAreas: data,
            isLoading: { ...state.isLoading, routineApplicationAreas: false },
            errors: { ...state.errors, routineApplicationAreas: null },
          });
          return data;
        } catch (error) {
          set({
            isLoading: { ...state.isLoading, routineApplicationAreas: false },
            errors: { ...state.errors, routineApplicationAreas: error?.message ?? 'Failed' },
          });
          return [];
        }
      },

      /**
       * Fetch all reference data needed for product forms.
       * Skips any slice that already has data (e.g. rehydrated from localStorage after refresh).
       */
      fetchAllProductData: async () => {
        const state = get();
        const promises = [];
        if (state.ingredients.length === 0) promises.push(get().fetchIngredients());
        if (state.formulationTypes.length === 0) promises.push(get().fetchFormulationTypes());
        if (state.accreditationTypes.length === 0) promises.push(get().fetchAccreditationTypes());
        if (state.brands.length === 0) promises.push(get().fetchBrands());
        if (state.productCategories.length === 0) promises.push(get().fetchProductCategories());
        if (state.prescriptionFrequencies.length === 0) promises.push(get().fetchPrescriptionFrequencies());
        if (state.prescriptionApplicationPeriods.length === 0) promises.push(get().fetchPrescriptionApplicationPeriods());
        if (state.routineAmounts.length === 0) promises.push(get().fetchRoutineAmounts());
        if (state.routineApplicationAreas.length === 0) promises.push(get().fetchRoutineApplicationAreas());
        await Promise.all(promises);
      },

      clearAllData: () => {
        set({
          ...initialData,
          isLoading: initialLoading,
          errors: initialErrors,
        });
      },
    }),
    {
      name: 'craftmyskin-product-reference-data',
      partialize: (state) => ({
        ingredients: state.ingredients,
        formulationTypes: state.formulationTypes,
        accreditationTypes: state.accreditationTypes,
        brands: state.brands,
        productCategories: state.productCategories,
        prescriptionFrequencies: state.prescriptionFrequencies,
        prescriptionApplicationPeriods: state.prescriptionApplicationPeriods,
        routineAmounts: state.routineAmounts,
        routineApplicationAreas: state.routineApplicationAreas,
      }),
    }
  )
);
